import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { Box, Typography, IconButton, Tooltip, Paper } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { PdfFieldDto } from '@/core/models/form.model' // We'll need to define this or use the one from DTO
import { styled } from '@mui/material/styles'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'

// Worker setup
pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.js', import.meta.url).toString()

interface PdfFieldEditorProps {
    fileUrl: string
    fields: PdfFieldDto[]
    onChange: (fields: PdfFieldDto[]) => void
}

const FieldBox = styled(Box)<{ isSelected: boolean }>(({ theme, isSelected }) => ({
    position: 'absolute',
    border: `2px solid ${isSelected ? theme.palette.primary.main : theme.palette.secondary.main}`,
    backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.2)' : 'rgba(156, 39, 176, 0.1)',
    cursor: 'move',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
        border: `2px solid ${theme.palette.primary.main}`,
        zIndex: 10,
    },
}))

const ResizeHandle = styled(Box)({
    position: 'absolute',
    width: 10,
    height: 10,
    backgroundColor: 'white',
    border: '1px solid #666',
    bottom: -5,
    right: -5,
    cursor: 'se-resize',
    zIndex: 11,
})

const PageContainer = styled(Box)({
    position: 'relative',
    marginBottom: 20,
    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
})

export const PdfFieldEditor: React.FC<PdfFieldEditorProps> = ({ fileUrl, fields, onChange }) => {
    const [numPages, setNumPages] = useState<number>(0)
    const [pageDimensions, setPageDimensions] = useState<{ [key: number]: { width: number; height: number } }>({})
    const [selectedField, setSelectedField] = useState<number | null>(null)

    // Dragging state
    const [dragState, setDragState] = useState<{
        isDragging: boolean
        isResizing: boolean
        fieldIndex: number
        startX: number
        startY: number
        startLeft: number
        startTop: number
        startWidth: number
        startHeight: number
    } | null>(null)

    const containerRef = useRef<HTMLDivElement>(null)

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages)
    }

    const onPageLoadSuccess = (page: any) => {
        setPageDimensions(prev => ({
            ...prev,
            [page.pageNumber]: { width: page.width, height: page.height }
        }))
    }

    // Convert 0-1000 scale to pixels
    const toPixels = (val: number, max: number) => (val / 1000) * max
    // Convert pixels to 0-1000 scale
    const toScale = (val: number, max: number) => Math.round((val / max) * 1000)

    const handleMouseDown = (e: React.MouseEvent, index: number, isResize: boolean) => {
        e.stopPropagation()
        const field = fields[index]
        const pageDim = pageDimensions[field.page]
        if (!pageDim) return

        setSelectedField(index)

        // Calculate current pixel values
        const pixelX = toPixels(field.x, pageDim.width)
        const pixelY = toPixels(field.y, pageDim.height)
        const pixelW = toPixels(field.width, pageDim.width)
        const pixelH = toPixels(field.height, pageDim.height)

        setDragState({
            isDragging: !isResize,
            isResizing: isResize,
            fieldIndex: index,
            startX: e.clientX,
            startY: e.clientY,
            startLeft: pixelX,
            startTop: pixelY,
            startWidth: pixelW,
            startHeight: pixelH,
        })
    }

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!dragState) return

        const field = fields[dragState.fieldIndex]
        const pageDim = pageDimensions[field.page]
        if (!pageDim) return

        const deltaX = e.clientX - dragState.startX
        const deltaY = e.clientY - dragState.startY

        const newFields = [...fields]
        const currentField = { ...newFields[dragState.fieldIndex] }

        if (dragState.isDragging) {
            const newPixelX = Math.max(0, Math.min(dragState.startLeft + deltaX, pageDim.width - dragState.startWidth))
            const newPixelY = Math.max(0, Math.min(dragState.startTop + deltaY, pageDim.height - dragState.startHeight))

            currentField.x = toScale(newPixelX, pageDim.width)
            currentField.y = toScale(newPixelY, pageDim.height)
        } else if (dragState.isResizing) {
            const newPixelW = Math.max(20, dragState.startWidth + deltaX) // Min width 20px
            const newPixelH = Math.max(20, dragState.startHeight + deltaY) // Min height 20px

            currentField.width = toScale(newPixelW, pageDim.width)
            currentField.height = toScale(newPixelH, pageDim.height)
        }

        newFields[dragState.fieldIndex] = currentField
        console.log('PdfFieldEditor: Field updated', currentField)
        onChange(newFields)
    }, [dragState, fields, onChange, pageDimensions])

    const handleMouseUp = useCallback(() => {
        setDragState(null)
    }, [])

    useEffect(() => {
        if (dragState) {
            window.addEventListener('mousemove', handleMouseMove)
            window.addEventListener('mouseup', handleMouseUp)
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove)
            window.removeEventListener('mouseup', handleMouseUp)
        }
    }, [dragState, handleMouseMove, handleMouseUp])

    const handleDeleteField = (index: number, e: React.MouseEvent) => {
        e.stopPropagation()
        const newFields = fields.filter((_, i) => i !== index)
        onChange(newFields)
        setSelectedField(null)
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', bgcolor: '#f5f5f5', p: 2, overflowY: 'auto', maxHeight: '70vh' }} ref={containerRef}>
            <Document
                file={fileUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={<Typography>Loading PDF...</Typography>}
            >
                {Array.from(new Array(numPages), (el, index) => (
                    <PageContainer key={`page_${index + 1}`}>
                        <Page
                            pageNumber={index + 1}
                            onLoadSuccess={onPageLoadSuccess}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            width={800} // Fixed width for consistency
                        />
                        {/* Overlay Fields */}
                        {pageDimensions[index + 1] && fields
                            .map((field, i) => ({ ...field, originalIndex: i }))
                            .filter(f => f.page === index + 1)
                            .map((field) => {
                                const pageDim = pageDimensions[index + 1]
                                const left = toPixels(field.x, pageDim.width)
                                const top = toPixels(field.y, pageDim.height)
                                const width = toPixels(field.width, pageDim.width)
                                const height = toPixels(field.height, pageDim.height)

                                return (
                                    <FieldBox
                                        key={field.originalIndex}
                                        isSelected={selectedField === field.originalIndex}
                                        style={{
                                            left,
                                            top,
                                            width,
                                            height,
                                        }}
                                        onMouseDown={(e) => handleMouseDown(e, field.originalIndex, false)}
                                    >
                                        <Tooltip title={`${field.label} (${field.type})`}>
                                            <Typography variant="caption" sx={{ fontSize: 10, overflow: 'hidden', whiteSpace: 'nowrap', px: 0.5 }}>
                                                {field.label}
                                            </Typography>
                                        </Tooltip>

                                        {selectedField === field.originalIndex && (
                                            <>
                                                <ResizeHandle onMouseDown={(e) => handleMouseDown(e, field.originalIndex, true)} />
                                                <IconButton
                                                    size="small"
                                                    sx={{ position: 'absolute', top: -25, right: -25, bgcolor: 'white', '&:hover': { bgcolor: '#ffebee' } }}
                                                    onClick={(e) => handleDeleteField(field.originalIndex, e)}
                                                >
                                                    <DeleteIcon fontSize="small" color="error" />
                                                </IconButton>
                                            </>
                                        )}
                                    </FieldBox>
                                )
                            })}
                    </PageContainer>
                ))}
            </Document>
        </Box>
    )
}
